// maomi/src/components/CatModification.js

import React, { useState, useEffect } from 'react';
import { Form, Input, Upload, DatePicker, message, Row, Col, Slider, Modal, Tooltip, Checkbox, Select, Button, Switch } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { AutoFixHigh as AutoFixHighIcon, Loop as LoopIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import { get, post, put, del } from '../utils/api';
import GradientButton from '../components/GradientButton';
import SelectCity from '../components/SelectCity';
import dayjs from "dayjs";
import './css/CatModification.css';

const { Option } = Select;

const CatModification = ({ visible, onClose, cat, onUpdate }) => {
  const [form] = Form.useForm();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [breeds, setBreeds] = useState([]);
  const [photoUrls, setPhotoUrls] = useState(cat.photos || []);
  const [is_private, setis_private] = useState(cat.is_private || false);
  const [vaccinationStatus, setVaccinationStatus] = useState({
    first: cat.first_vaccination_date ? true : false,
    second: cat.second_vaccination_date ? true : false,
    third: cat.third_vaccination_date ? true : false,
    rabies: cat.rabies_vaccination_date ? true : false,
  });
  const [birthDatePickerOpen, setBirthDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchBreeds();
  }, []);

  const fetchBreeds = async () => {
    try {
      const data = await get('/cat/breeds/');
      setBreeds(data);
    } catch (error) {
      message.error('Failed to fetch breeds');
    }
  };

  const handleUpdate = async (values) => {
    values.birth_date = values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null;
    values.first_vaccination_date = values.first_vaccination_date ? values.first_vaccination_date.format('YYYY-MM-DD') : null;
    values.second_vaccination_date = values.second_vaccination_date ? values.second_vaccination_date.format('YYYY-MM-DD') : null;
    values.third_vaccination_date = values.third_vaccination_date ? values.third_vaccination_date.format('YYYY-MM-DD') : null;
    values.rabies_vaccination_date = values.rabies_vaccination_date ? values.rabies_vaccination_date.format('YYYY-MM-DD') : null;
    const data = { ...values, photos: photoUrls, is_private: is_private };

    try {
      await put(`/cat/${cat.id}/update/`, data);
      message.success('猫咪信息更新成功!');
      onUpdate();
      onClose();
    } catch (error) {
      message.error(error.response ? error.response.data.status : '发生错误');
      setFeedbackMessage('发生错误');
    }
  };

  const handleUploadChange = async (info) => {
    if (info.file.status === 'done') {
      const url = info.file.response.url;
      setPhotoUrls([...photoUrls, url]);
      message.success(`${info.file.name} 上传成功`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} 上传失败`);
    }
  };

  const handlePrivacyToggle = (checked) => {
    setis_private(checked);  // Update the privacy setting in the state
  };

  const handleDelete = async () => {
    try {
      await del(`/cat/${cat.id}/delete/`);
      message.success('猫咪删除成功!');
      onUpdate();
      onClose();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const generateCatName = async () => {
    try {
      const response = await post('/cat/generate_cat_name/', {});
      form.setFieldsValue({ name: response.name });
    } catch (error) {
      message.error('生成名字失败');
    }
  };

  const handleDateChange = (field, date) => {
    if (date) {
      setVaccinationStatus(prevState => ({ ...prevState, [field]: true }));
      form.setFieldsValue({ [`${field}_vaccination_date`]: date });
    } else {
      setVaccinationStatus(prevState => ({ ...prevState, [field]: false }));
      form.setFieldsValue({ [`${field}_vaccination_date`]: null });
    }
  };
  
  const handleCheckboxChange = (field, checked) => {
    setVaccinationStatus(prevState => ({ ...prevState, [field]: checked }));
    if (!checked) {
      form.setFieldsValue({ [`${field}_vaccination_date`]: null });
    }
  };

  return (
    <Modal
      title={
        <div className="cat-modification-header">
          <span className="ant-modal-title">修改猫咪信息</span>
          <div className="header-actions">
          <Tooltip title="删除此猫咪信息">
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: 'red' }} />}
                onClick={() => {
                  Modal.confirm({
                    title: '确认删除',
                    content: '确定要删除这只猫咪的信息吗？',
                    okText: '确定',
                    cancelText: '取消',
                    onOk: handleDelete,
                  });
                }}
              />
            </Tooltip>
            <div className="privacy-toggle">
            <Tooltip title="猫咪信息隐私保护，开启后此猫咪仅您可见">
              <Switch
                checked={is_private}
                onChange={handlePrivacyToggle}
                checkedChildren="私密"
                unCheckedChildren="公开"
              />
            </Tooltip>
            </div>
          </div>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        className="cat-registration-form"
        initialValues={{
          name: cat.name,
          breed_id: cat.breed ? cat.breed.id : null,
          sex: cat.sex,
          birth_city: cat.birth_city,
          birth_date: cat.birth_date ? dayjs(cat.birth_date) : null,
          cuteness: cat.cuteness,
          health: cat.health,
          clinginess: cat.clinginess,
          gluttony: cat.gluttony,
          sleepiness: cat.sleepiness,
          pettiness: cat.pettiness,
          first_vaccination_date: cat.first_vaccination_date ? dayjs(cat.first_vaccination_date) : null,
          second_vaccination_date: cat.second_vaccination_date ? dayjs(cat.second_vaccination_date) : null,
          third_vaccination_date: cat.third_vaccination_date ? dayjs(cat.third_vaccination_date) : null,
          rabies_vaccination_date: cat.rabies_vaccination_date ? dayjs(cat.rabies_vaccination_date) : null,
        }}
        onFinish={handleUpdate}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="photos"
              label="照片"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
            >
              <Upload
                listType="picture-card"
                className="photo-upload"
                onChange={handleUploadChange}
                multiple={true}
                action={`${API_BASE_URL}/cat/upload/`}
                maxCount={10}
                headers={{
                  'Authorization': `Token ${localStorage.getItem('token')}` // For file upload we need to use the token directly
                }}
              >
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>点击上传</div>
                </div>
              </Upload>
            </Form.Item>
            <div className="feedback-section">
              {feedbackMessage}
            </div>
            <Form.Item
              name="breed_id"
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>品种</span>
                  <Tooltip title="AI品种检测">
                    <AutoFixHighIcon
                      style={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                </div>
              }
            >
              <Select placeholder="请选择品种">
                {breeds.map(breed => (
                  <Option key={breed.id} value={breed.id}>
                    {breed.name_cn} ({breed.name_en})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={16}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="name"
                  label="名字"
                  rules={[{ required: true, message: '请输入名字!' }]}
                >
                  <Input
                    placeholder="名字"
                    addonAfter={
                      <Tooltip title="一键生成名字">
                        <LoopIcon
                          style={{ cursor: 'pointer' }}
                          onClick={generateCatName}
                        />
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="sex"
                  label="性别"
                  rules={[{ required: true, message: '请选择性别!' }]}
                >
                  <Select placeholder="请选择性别">
                    <Option value="M">公猫</Option>
                    <Option value="F">母猫</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="birth_city"
                  label="出生城市"
                  rules={[{ required: true, message: '请输入出生城市!' }]}
                >
                  <SelectCity value={cat.birth_city} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="birth_date"
                  label="出生日期"
                  rules={[{ required: true, message: '请输入出生日期!' }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="YYYY-MM-DD" 
                    open={birthDatePickerOpen}
                    onOpenChange={(open) => setBirthDatePickerOpen(open)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Checkbox 
                  checked={vaccinationStatus.first} 
                  onChange={(e) => handleCheckboxChange('first', e.target.checked)}
                >
                  猫三联①
                </Checkbox>
                <Form.Item name="first_vaccination_date">
                  <DatePicker 
                    placeholder="选择日期" 
                    onChange={(date) => handleDateChange('first', date)}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Checkbox 
                  checked={vaccinationStatus.second} 
                  onChange={(e) => handleCheckboxChange('second', e.target.checked)}
                >
                  猫三联②
                </Checkbox>
                <Form.Item name="second_vaccination_date">
                  <DatePicker 
                    placeholder="选择日期" 
                    onChange={(date) => handleDateChange('second', date)}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Checkbox 
                  checked={vaccinationStatus.third} 
                  onChange={(e) => handleCheckboxChange('third', e.target.checked)}
                >
                  猫三联③
                </Checkbox>
                <Form.Item name="third_vaccination_date">
                  <DatePicker 
                    placeholder="选择日期" 
                    onChange={(date) => handleDateChange('third', date)}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Checkbox 
                  checked={vaccinationStatus.rabies} 
                  onChange={(e) => handleCheckboxChange('rabies', e.target.checked)}
                >
                  狂犬病
                </Checkbox>
                <Form.Item name="rabies_vaccination_date">
                  <DatePicker 
                    placeholder="选择日期" 
                    onChange={(date) => handleDateChange('rabies', date)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="cuteness"
                  label="可爱值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="health"
                  label="健康值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="clinginess"
                  label="粘人值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="gluttony"
                  label="吃货值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="sleepiness"
                  label="贪睡值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="pettiness"
                  label="撒泼值"
                >
                  <Slider min={0} max={10} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form.Item>
          <GradientButton type="primary" htmlType="submit" className="reg-button">
            保存修改
          </GradientButton>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CatModification;
